import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';

type Granularity = 'YEAR' | 'QUARTER' | 'MONTH';
interface Period { periodKey: string; count: number; cumulative: number; growthPctVsPrev: number | null }
interface GrowthResult {
  granularity: Granularity;
  totalAds: number;
  firstPeriod: string | null;
  lastPeriod: string | null;
  cagrPct: number | null;
  periods: Period[];
}

/** §15 year-wise growth + §16 historical playback series. */
export function TimelineScreen(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [granularity, setGranularity] = useState<Granularity>('MONTH');
  const [growth, setGrowth] = useState<GrowthResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: GrowthResult }>(`/v1/geo-intel/timeline/growth?granularity=${granularity}`, { token })
      .then((r) => { if (alive) { setGrowth(r.data); setError(null); } })
      .catch((e) => {
        if (!alive) return;
        setError(e instanceof ApiError && e.status === 403
          ? t('m16report.timeline.forbidden')
          : (e as Error).message);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, granularity]);

  const maxCumulative = growth ? growth.periods.reduce((m, p) => Math.max(m, p.cumulative), 0) : 0;

  return (
    <main aria-label="geo-timeline">
      <h1>{t('m16report.timeline.title')}</h1>
      {error && <p role="alert" aria-label="timeline-error">{error}</p>}
      <label>{t('m16report.timeline.granularity')}
        <select aria-label="granularity" value={granularity} onChange={(e) => setGranularity(e.target.value as Granularity)}>
          <option value="YEAR">{t('m16report.timeline.gran.year')}</option>
          <option value="QUARTER">{t('m16report.timeline.gran.quarter')}</option>
          <option value="MONTH">{t('m16report.timeline.gran.month')}</option>
        </select>
      </label>
      {loading ? <p>{t('common.loading')}</p> : growth && (
        <>
          <p aria-label="growth-summary">
            {t('m16report.timeline.summary', { total: growth.totalAds, first: growth.firstPeriod ?? '—', last: growth.lastPeriod ?? '—', cagr: growth.cagrPct ?? '—' })}
          </p>
          <DataTable
            ariaLabel="growth-table"
            emptyMessage={t('m16report.timeline.empty')}
            rowKey={(p) => p.periodKey}
            rows={growth.periods}
            columns={[
              { key: 'period', header: t('m16report.timeline.col.period'), render: (p) => p.periodKey },
              { key: 'new', header: t('m16report.timeline.col.new'), render: (p) => p.count },
              { key: 'cumulative', header: t('m16report.timeline.col.cumulative'), render: (p) => p.cumulative },
              { key: 'growth', header: t('m16report.timeline.col.growth'), render: (p) => p.growthPctVsPrev ?? '—' },
              { key: 'trend', header: t('m16report.timeline.col.trend'), render: (p) => (
                <span style={{ display: 'inline-block', height: 8, background: 'var(--color-success)', width: `${maxCumulative ? (p.cumulative / maxCumulative) * 100 : 0}%` }} />
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
